import { useCallback, useEffect, useRef, useState } from "react";
import { PagingProps } from "../types/paging-props";
import { Paging } from "../types/paging";
import { PaginatedResult } from "../types/paginated-result";

interface PaginatorProps<T> {
    load: (page: number, pageSize: number) => Promise<PaginatedResult<T> | null>;
    DataComponent: React.FunctionComponent<PagingProps<T>>;
    defaultPageSize?: number;
    defaultPage?: number;
}

export function Paginator<T>({ load, DataComponent, defaultPage, defaultPageSize }: PaginatorProps<T>) {
    const isMounted = useRef(true);
    useEffect(() => { return () => { isMounted.current = false; } }, []);
    const ifMounted = useCallback((callback: () => void) => { if (isMounted.current) { callback(); } }, []);

    const [data, setData] = useState<T[] | null>();
    const [isLoading, setLoading] = useState(true);

    const [paging, setPaging] = useState<Paging>({ page: defaultPage ?? 0, pageSize: defaultPageSize ?? 0, totalPages: 0, totalRows: 0 });
    const setPageSize = useCallback((pageSize: number) => {
        setPaging(prev => {
            return {
                ...prev,
                pageSize: pageSize
            }
        })
    }, [])

    const setPage = useCallback((page: number) => {
        setPaging(prev => {
            return {
                ...prev,
                page: page
            }
        })
    }, [])

    const loadData = useCallback(async () => {
        setLoading(true);
        load(paging.page, paging.pageSize)
            .then(result => {
                ifMounted(() => {
                    setData(result?.data || null);
                    setPaging(prev => result ? {
                        page: prev.page,
                        pageSize: prev.pageSize,
                        totalPages: result.paging.totalPages,
                        totalRows: result.paging.totalRows
                    } : prev);
                    setLoading(false);
                })
            })
    }, [paging.page, paging.pageSize, load]);

    useEffect(() => {
        loadData()
    }, [loadData]);

    return <DataComponent
        data={data}
        isLoading={isLoading}
        setPage={setPage}
        setPageSize={setPageSize}
        reload={loadData}
        {...paging}
    />
}